import { useEffect, useState } from 'react'
import Radiobutton from '@web-ui-kit/radiobutton'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import * as Sc from './SelectGroup.style'
import SelectGroupSkeleton from './SelectGroup.skeleton'
import PageWrapper from 'components/PageWrapper'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import { hideLoader, updateFullScreen } from 'store/actions/drawer.actions'
import { ROUTES } from 'routes/paths'
import { USER_KEYS } from 'hooks/user'
import {
  STUDENT_KEYS,
  useGetStudent,
  useGetStudentClassroomsByCourse,
  usePostSaveStudentClassroom,
} from 'hooks/student'
import { TYPES_USER_ROLES, TYPE_VALIDATION_STATUS } from 'utils/constants'
import { TYPES_ALERTS } from 'utils/constants/global'
import { getUrlParams } from 'utils'
import Page404 from 'components/Page404'
import { fetchStudentListAction } from 'store/actions/student.actions'

const SelectGroup = () => {
  const [selectedGroupId, setSelectedGrouopId] = useState('')
  const dispatch = useDispatch()
  const addFeedback = useSnackBarFeedback()
  const { t } = useTranslation()
  const { id: studentId } = useParams()
  const { push } = useHistory()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const urlPath = getUrlParams('origin')
  const originPath = urlPath ?? ROUTES.BASE
  const { requestValidationStatus, role: userRole } = queryClient.getQueryData({
    queryKey: [USER_KEYS.GET_USER],
  })

  const isRelative = userRole === TYPES_USER_ROLES.RELATIVE
  const { mutate: mutateAddClassroom, isLoading: isLoadingAddClassroom } =
    usePostSaveStudentClassroom()

  const {
    data: student,
    isLoading: isLoadingStudent,
    isSuccess: isSuccessStudent,
  } = useGetStudent({
    userId: studentId,
  })

  const { course, school, classroom } = student || {}

  const isEnabledClassrooms = Boolean(course?.id && school?.id && !classroom)

  const {
    data: classrooms,
    isLoading: isLoadingClassrooms,
    isSuccess,
  } = useGetStudentClassroomsByCourse({
    courseId: course?.id,
    enabled: Boolean(isSuccessStudent && isEnabledClassrooms),
    schoolId: school?.id,
  })

  useEffect(() => {
    const requestValidation = isRelative
      ? student?.requestValidationStatus
      : requestValidationStatus

    if (
      requestValidation === TYPE_VALIDATION_STATUS.PENDING ||
      requestValidation === TYPE_VALIDATION_STATUS.VALIDATED
    ) {
      push(originPath)
    } else {
      dispatch(hideLoader(true))
      dispatch(updateFullScreen(true))
    }
    return () => {
      dispatch(hideLoader(false))
      dispatch(updateFullScreen(false))
    }
  }, [])

  const handleOnChange = (event) => {
    setSelectedGrouopId(event.target.value)
  }

  const handleRedirect = () => {
    const redirectUrl = originPath === pathname ? ROUTES.BASE : originPath
    push(redirectUrl)
  }
  const handleOnSubmit = () => {
    mutateAddClassroom(
      {
        classroomId: selectedGroupId,
        studentId,
      },
      {
        onError: () => {
          addFeedback({
            dataTestId: 'error-feedback-school-code',
            id: 'error-feedback-school-code',
            text: t('Ha ocurrido un problema'),
            variant: TYPES_ALERTS.ERROR,
          })
        },
        onSuccess: () => {
          if (!isRelative) {
            queryClient.invalidateQueries({
              queryKey: [USER_KEYS.GET_USER],
            })
          }
          if (isRelative) {
            queryClient.invalidateQueries({
              queryKey: [STUDENT_KEYS.STUDENT],
            })
            dispatch(fetchStudentListAction(true))
          }
          addFeedback({
            dataTestId: 'success-feedback-course',
            id: 'success-feedback-course',
            text: t('Los cambios se han realizado con éxito'),
            variant: TYPES_ALERTS.SUCCESS,
          })
          handleRedirect()
        },
      }
    )
  }

  const isEnabledErrorPage = Boolean(isSuccessStudent && !isEnabledClassrooms)

  const { classrooms: studentClassrooms } = classrooms || {}
  const isLoading = Boolean(isLoadingStudent || isLoadingClassrooms)

  if (isEnabledErrorPage) {
    return <Page404 goTo={() => push(ROUTES.BASE)} />
  }
  if (isLoading) {
    return <SelectGroupSkeleton />
  }
  if (isSuccess) {
    return (
      <PageWrapper
        button={{
          disabled: Boolean(!selectedGroupId || isLoadingAddClassroom),
          loading: isLoadingAddClassroom,
          onClick: handleOnSubmit,
          text: t('Finalizar'),
        }}
        cancelButton={{
          onClick: handleRedirect,
          text: t('Indicarlo más tarde'),
        }}
        subtitle={t('Si todavía no lo sabes, pídeselo a tu profesor/a')}
        title={t('Selecciona tu grupo')}
      >
        <Sc.MainContent>
          {studentClassrooms.map((classroom) => (
            <Radiobutton
              checked={selectedGroupId === classroom.id}
              id={classroom.id}
              key={classroom.id}
              label={`${t('Grupo')} ${classroom.name}`}
              name={classroom.name}
              onChange={handleOnChange}
              value={classroom.id}
            />
          ))}
        </Sc.MainContent>
      </PageWrapper>
    )
  }

  return null
}

export default SelectGroup
